import { useCallback } from 'react';
import { useQuizState } from './useQuizState';
import { useQuizQuestions } from './useQuizQuestions';
import { toast } from 'sonner';
import { supabase, saveQuizHistory } from '@/lib/supabase';
import { useGamification } from '@/hooks/useGamification';
import { categories } from '@/data/questions';
import { useRealTimeAdaptiveDifficulty } from '@/hooks/useRealTimeAdaptiveDifficulty';
import { Question } from '@/types/quiz';
import { generateUUID } from '@/utils/uuid';

export function useQuiz() {
  const {
    state,
    setState,
    resetProgress,
    setSubject: setQuizSubject,
    setCompletedQuestions,
    setView,
    initialState
  } = useQuizState();

  const {
    questions,
    questionsLoading,
    error,
    subjectQuestions,
    setQuestions,
    adaptivePool,
    usedQuestionIds,
    getNextQuestions,
    markQuestionAsUsed,
    resetUsedQuestions,
    addQuestionSnapshot,
    updateQuestionAnswer,
    getQuestionSnapshots
  } = useQuizQuestions(state);

  const { checkAchievements, updateStreak } = useGamification();

  const adaptiveDifficulty = useRealTimeAdaptiveDifficulty(state, setState, {
    minQuestionsBeforeAdjustment: 1,
    showNotifications: true,
    fastAnswerThresholdMs: 10000,
    startWithMedium: true,
    scoreMultipliers: { 
      easy: 1, 
      medium: 2, 
      hard: 3 
    }
  });

  const handleSubjectSelect = useCallback(async (subject: string) => {
    console.log('Handling subject selection:', subject);
    setQuizSubject(subject);
    resetUsedQuestions();
    
    setView('quiz');
    
    setState(prev => ({ 
      ...prev, 
      questionIndex: 0,
      difficulty: 'medium',
      completedQuestions: [],
      answers: [],
      lastSessionId: generateUUID()
    }));
    toast.success(`Selected subject: ${subject}`);
  }, [setQuizSubject, setState, resetUsedQuestions, setView]);

  const updateDifficulty = useCallback(async (score: number, subject: string, currentDifficulty: "easy" | "medium" | "hard") => {
    try {
      console.log('Current quiz performance:', {
        score,
        subject,
        currentDifficulty
      });

      const { data, error } = await supabase.rpc('calculate_adaptive_difficulty', {
        p_user_id: (await supabase.auth.getUser()).data.user?.id,
        p_subject: subject,
        p_current_difficulty: currentDifficulty
      });

      if (error) {
        console.error('Error calculating adaptive difficulty:', error);
        return currentDifficulty;
      }

      const newDifficulty = data as string;
      const validatedDifficulty = ['easy', 'medium', 'hard'].includes(newDifficulty.toLowerCase())
        ? newDifficulty.toLowerCase() as "easy" | "medium" | "hard"
        : currentDifficulty;
      
      console.log('Difficulty adjustment result:', {
        previousDifficulty: currentDifficulty,
        newDifficulty: validatedDifficulty,
        changed: validatedDifficulty !== currentDifficulty
      });

      if (validatedDifficulty !== currentDifficulty) {
        toast.info(`Difficulty adjusted to ${validatedDifficulty} based on your performance`);
      }

      return validatedDifficulty;
    } catch (error) {
      console.error('Error in updateDifficulty:', error);
      return currentDifficulty;
    }
  }, []);

  const handleQuizComplete = useCallback(async (answers: (number | null)[], completedQuestions: Question[]) => {
    try {
      console.log("Quiz complete with questions:", completedQuestions);
      console.log("Quiz complete with answers:", answers);
      console.log("Completed question difficulties:", completedQuestions.map(q => q.difficulty));
      console.log("Completed question categories:", completedQuestions.map(q => q.category));
      console.log("Completed question subjects:", completedQuestions.map(q => q.subject));
      
      const questionSnapshots = getQuestionSnapshots();
      console.log("Quiz complete with question snapshots:", questionSnapshots);
      
      const frozenSnapshots = JSON.parse(JSON.stringify(questionSnapshots));
      
      for (let i = 0; i < frozenSnapshots.length; i++) {
        if (!frozenSnapshots[i].category || frozenSnapshots[i].category === '') {
          frozenSnapshots[i].category = state.category;
        }
        if (!frozenSnapshots[i].subject || frozenSnapshots[i].subject === '') {
          frozenSnapshots[i].subject = state.subject;
        }
      }
      
      setCompletedQuestions(frozenSnapshots);
      
      const validQuestions: Question[] = [];
      const validAnswers: number[] = [];
      
      for (let i = 0; i < frozenSnapshots.length; i++) {
        if (frozenSnapshots[i].selectedAnswer !== null && frozenSnapshots[i].selectedAnswer !== undefined) {
          validQuestions.push(frozenSnapshots[i]);
          validAnswers.push(frozenSnapshots[i].selectedAnswer as number);
        }
      }
      
      console.log("After filtering, valid questions:", validQuestions.length);
      console.log("After filtering, valid answers:", validAnswers);
      console.log("Valid question difficulties:", validQuestions.map(q => q.difficulty));
      console.log("Valid question categories:", validQuestions.map(q => q.category));
      console.log("Valid question subjects:", validQuestions.map(q => q.subject));
      
      const finalQuestions = validQuestions.slice(0, state.questionsCount);
      const finalAnswers = validAnswers.slice(0, state.questionsCount);
      
      if (finalQuestions.length === 0 || finalAnswers.length === 0) {
        console.error('No valid questions or answers to save');
        toast.error('No valid quiz data to save');
        return;
      }
      
      const correctAnswers = finalAnswers.reduce((sum, answer, index) => {
        return sum + (answer === finalQuestions[index].correctanswer ? 1 : 0);
      }, 0);

      const score = finalQuestions.length > 0 ? (correctAnswers / finalQuestions.length) * 100 : 0;
      const userId = (await supabase.auth.getUser()).data.user?.id;

      if (!userId) {
        console.error('No user ID found');
        return;
      }

      console.log('Quiz completion details:', {
        score,
        correctAnswers,
        totalQuestions: finalQuestions.length,
        subject: state.subject,
        category: state.category,
        currentDifficulty: state.difficulty,
        questionDifficulties: finalQuestions.map(q => q.difficulty),
        questionCategories: finalQuestions.map(q => q.category),
        questionSubjects: finalQuestions.map(q => q.subject)
      });

      const newDifficulty = await updateDifficulty(score, state.subject!, state.difficulty);

      const now = new Date().toISOString();

      const quizResultData = {
        user_id: userId,
        score: correctAnswers,
        total_questions: finalQuestions.length,
        percentage: Math.round(score),
        category: state.category,
        subject: state.subject,
        difficulty: newDifficulty,
        questions: finalQuestions,
        answers: finalAnswers
      };

      console.log('Saving quiz results:', quizResultData);
      console.log('Questions in quiz results with difficulties:', finalQuestions.map(q => ({id: q.id, difficulty: q.difficulty, category: q.category, subject: q.subject})));

      const { data: quizResultResponse, error: quizResultError } = await supabase
        .from('quiz_results')
        .insert([quizResultData])
        .select()
        .single();

      if (quizResultError) {
        console.error('Error saving quiz results:', quizResultError);
        toast.error('Failed to save quiz results');
      } else {
        console.log('Quiz results saved with session ID:', quizResultResponse.id);
        
        try {
          const historySuccess = await saveQuizHistory(
            quizResultResponse.id,
            finalQuestions,
            finalAnswers,
            state.subject,
            state.category,
            userId
          );
          
          if (!historySuccess) {
            console.error('Failed to save detailed quiz history');
            toast.error('Could not save your quiz history');
          }
        } catch (historyError) {
          console.error('Error saving quiz history:', historyError);
          toast.error('Error saving quiz history');
        }
        
        setState(prev => ({
          ...prev,
          lastSessionId: quizResultResponse.id,
          completedQuestions: finalQuestions
        }));
      }

      const { error: perfError } = await supabase
        .from('performance_history')
        .insert([{
          user_id: userId,
          score: Math.round(score),
          correct_answers: correctAnswers,
          total_questions: finalQuestions.length,
          subject: state.subject,
          category: state.category,
          difficulty: newDifficulty,
          created_at: now
        }]);

      if (perfError) {
        console.error('Error saving performance:', perfError);
        toast.error('Failed to save quiz results');
        return;
      }

      const { error: sessionError } = await supabase
        .from('learning_sessions')
        .insert([{
          user_id: userId,
          correct_answers: correctAnswers,
          total_questions: finalQuestions.length,
          subject: state.subject,
          category: state.category,
          difficulty: newDifficulty,
          start_time: now,
          end_time: now,
          time_per_question: {}
        }]);

      if (sessionError) {
        console.error('Error saving learning session:', sessionError);
        toast.error('Failed to save learning session');
        return;
      }

      await Promise.all([
        checkAchievements(score, finalQuestions.length, newDifficulty),
        updateStreak(state.subject)
      ]);

      setState(prev => ({
        ...prev,
        difficulty: newDifficulty
      }));

      console.log('Quiz results saved successfully with new difficulty:', newDifficulty);
      toast.success('Quiz results saved!');
    } catch (error) {
      console.error('Error in handleQuizComplete:', error);
      toast.error('Failed to save quiz results');
    }
  }, [state.subject, state.category, state.difficulty, state.questionsCount, checkAchievements, updateStreak, updateDifficulty, setState, setCompletedQuestions, getQuestionSnapshots]);

  const handleNext = useCallback(async () => {
    const currentQuestion = questions[state.questionIndex];
    const currentQuestionIndex = state.questionIndex;
    const currentAnswer = state.answers[currentQuestionIndex];
    
    if (!currentQuestion) {
      console.error('No current question found');
      return;
    }
    
    addQuestionSnapshot(currentQuestion, currentQuestionIndex);
    
    if (currentAnswer !== null && currentAnswer !== undefined) {
      updateQuestionAnswer(currentQuestionIndex, currentAnswer);
    }
    
    if (currentQuestion && currentQuestion.id) {
      markQuestionAsUsed(currentQuestion.id as number);
    }
    
    if (currentQuestion && currentAnswer !== undefined) {
      const isCorrect = currentAnswer === currentQuestion.correctanswer;
      
      console.log(`Answer ${currentAnswer} selected for question ${currentQuestion.id}. Correct: ${isCorrect}`);
      console.log(`Question difficulty: ${currentQuestion.difficulty}`);
      
      if (isCorrect) {
        adaptiveDifficulty.recordCorrectAnswer();
      } else {
        adaptiveDifficulty.recordIncorrectAnswer();
      }
    }

    const completedQuestions = getQuestionSnapshots();
    
    const maxQuestionsReached = currentQuestionIndex >= state.questionsCount - 1;
    console.log(`Current question index: ${currentQuestionIndex}, Max questions: ${state.questionsCount}, Reached max: ${maxQuestionsReached}`);
    
    if (maxQuestionsReached) {
      const answers = completedQuestions.map(q => q.selectedAnswer !== undefined ? q.selectedAnswer : null);
      
      console.log('Quiz completed with these questions and their difficulties:');
      completedQuestions.forEach((q, i) => {
        console.log(`Question ${i+1}: ID=${q.id}, Difficulty=${q.difficulty}`);
      });
      
      await handleQuizComplete(answers, completedQuestions);
      setView("results");
      
      const correctAnswers = completedQuestions.filter(q => 
        q.isCorrect === true
      ).length;
      
      const performance = correctAnswers / completedQuestions.length;
      
      if (performance > 0.8) {
        toast.success(`Great job! You performed well on ${state.subject} questions.`);
        if (state.difficulty !== 'hard') {
          toast.info(`Try the next level: increase difficulty to '${state.difficulty === 'easy' ? 'medium' : 'hard'}'`);
        }
      } else if (performance < 0.5) {
        toast.info(`You might benefit from reviewing ${state.subject} fundamentals.`);
        toast.info(`Try our flashcards or AI tutoring for additional help.`);
      } else {
        toast.info(`Keep practicing ${state.subject} to improve your score.`);
      }
    } else {
      setState(prev => ({ 
        ...prev, 
        questionIndex: prev.questionIndex + 1,
        selectedAnswer: null,
        showHint: false,
        currentHint: null
      }));
    }
  }, [state.questionIndex, state.answers, state.questionsCount, questions, handleQuizComplete, setState, adaptiveDifficulty, markQuestionAsUsed, addQuestionSnapshot, updateQuestionAnswer, getQuestionSnapshots, setView]);

  const handleResetProgress = useCallback(() => {
    resetProgress();
    resetUsedQuestions();
    toast.success('Progress has been reset');
  }, [resetProgress, resetUsedQuestions]);

  const availableCategories = categories.map((cat, index) => ({
    id: index + 1,
    name: cat.name,
    created_at: new Date().toISOString()
  }));

  if (!state.lastSessionId) {
    setState(prev => ({
      ...prev,
      lastSessionId: generateUUID()
    }));
  }

  return {
    state,
    setState,
    questions,
    questionsLoading,
    error,
    subjectQuestions,
    setQuestions,
    handleSubjectSelect,
    handleResetProgress,
    handleNext,
    availableCategories,
    categories,
    adaptiveDifficulty,
    setCompletedQuestions,
    adaptivePool,
    usedQuestionIds,
    initialState,
    setView
  };
}
